import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building, Calendar, MapPin, Users, Pencil, ExternalLink, CheckCircle, ChevronRight, GripVertical, Target, FileDown, Loader2, Mail, Send, Search, Linkedin, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useRFQ } from '../../hooks/useRFQs';
import AddRFQModal from './AddRFQModal';
import RFQQuestionsManager from './RFQQuestionsManager';
import MatchProfileCard from './matching/MatchProfileCard';
import ManualCandidateSearch from './matching/ManualCandidateSearch';
import TemplateSelector from './matching/TemplateSelector';
import { supabase } from '../../lib/supabaseClient';
import { formatDate } from '../../utils/dateFormatter';
import { resolveMergeTags, resolveMergeTagsHtml } from '../../utils/emailMergeTagResolver';
import DOMPurify from 'dompurify';
import DetailContainer from '../ui/detail-container';
import { cleanHTML, fixSelectionCriteriaNumbering, RICH_TEXT_PROSE_CLASSES as PROSE } from '../../utils/richTextRenderer';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const RFQDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { rfq, applications, loading, error, refetch } = useRFQ(id);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [downloadingDOCX, setDownloadingDOCX] = useState(false);
  const [docxError, setDocxError] = useState(null);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [draftingInclusion, setDraftingInclusion] = useState(false);
  const [manualSearchOpen, setManualSearchOpen] = useState(false);
  const [inclusionTemplate, setInclusionTemplate] = useState(null);
  const [generatingLinkedinDraft, setGeneratingLinkedinDraft] = useState(false);
  const [linkedinDraft, setLinkedinDraft] = useState(null); // { content }
  const [linkedinDraftError, setLinkedinDraftError] = useState(null);
  const [copiedLinkedinDraft, setCopiedLinkedinDraft] = useState(false);

  // Draggable table state
  const [orderedApplications, setOrderedApplications] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 45;

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Check Gmail connection status
  useEffect(() => {
    const checkGmailStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch(`${API_URL}/gmail/status`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setGmailConnected(data.connected);
        }
      } catch (err) {
        console.error('Error checking Gmail status:', err);
      }
    };

    checkGmailStatus();
  }, []);

  // Initialize ordered applications when applications load
  // Sort so 'withdrawn' and 'unsuitable' are at the end
  useEffect(() => {
    if (applications && applications.length > 0) {
      const sorted = [...applications].sort((a, b) => {
        const aIsLowPriority = ['withdrawn', 'unsuitable'].includes(a.status);
        const bIsLowPriority = ['withdrawn', 'unsuitable'].includes(b.status);

        if (aIsLowPriority && !bIsLowPriority) return 1;
        if (!aIsLowPriority && bIsLowPriority) return -1;
        return 0;
      });
      setOrderedApplications(sorted);
      setCurrentPage(1); // Reset to first page when applications change
    }
  }, [applications]);

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newApplications = [...orderedApplications];
    const draggedItem = newApplications[draggedIndex];
    newApplications.splice(draggedIndex, 1);
    newApplications.splice(index, 0, draggedItem);

    setOrderedApplications(newApplications);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Handle status change
  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: newStatus })
        .eq('id', applicationId);

      if (error) throw error;

      // Refresh applications
      await refetch();
    } catch (err) {
      console.error('Error updating application status:', err);
      alert('Failed to update status: ' + err.message);
    }
  };

  // Handle DOCX download
  const handleDownloadDOCX = async () => {
    try {
      setDownloadingDOCX(true);
      setDocxError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/generate-pd/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ rfqId: id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate DOCX');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PD-${rfq.ref_code || id}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccessMessage('DOCX downloaded successfully');
    } catch (err) {
      console.error('Error downloading DOCX:', err);
      setDocxError(err.message);
    } finally {
      setDownloadingDOCX(false);
    }
  };

  // Handle Request Inclusion
  const handleRequestInclusion = async () => {
    if (!gmailConnected) {
      alert('Please connect your Gmail account in Settings → Integrations first');
      navigate('/recruiter/settings/integrations');
      return;
    }

    if (!rfq.client?.primary_procurement_email) {
      alert('Client procurement email not found. Please add it in client details first.');
      return;
    }

    if (!inclusionTemplate) {
      alert('Please select an email template first');
      return;
    }

    try {
      setDraftingInclusion(true);

      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Prepare data for merge tag resolution
      const templateData = {
        rfq: {
          ref_code: rfq.ref_code,
          title: rfq.title,
          client_name: rfq.client?.name,
          client_close_datetime: rfq.client_close_datetime,
          locations: rfq.locations,
          clearance_required: rfq.clearance_required,
          work_arrangement: rfq.work_arrangement,
          estimated_start_date: rfq.estimated_start_date,
          initial_term_months: rfq.initial_term_months,
          description: rfq.description
        },
        client: {
          name: rfq.client?.name
        },
        application_url: `${process.env.REACT_APP_SITE_URL || 'https://appsytec.com'}/apply/${rfq.id}`
      };

      // Resolve merge tags in template
      const emailSubject = resolveMergeTags(inclusionTemplate.subject_template, templateData);
      const emailBodyHtml = resolveMergeTagsHtml(inclusionTemplate.body_template, templateData);

      // Sanitize final HTML
      const sanitizedBody = DOMPurify.sanitize(emailBodyHtml);

      // Draft email in Gmail
      const response = await fetch(`${API_URL}/gmail/draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          to: rfq.client.primary_procurement_email,
          subject: emailSubject,
          body: sanitizedBody
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create draft');
      }

      // Update RFQ status to 'inclusion_requested'
      const { error: updateError } = await supabase
        .from('rfqs')
        .update({ status: 'inclusion_requested' })
        .eq('id', id);

      if (updateError) throw updateError;

      setSuccessMessage('Draft created in Gmail and RFQ status updated to "Inclusion Requested"');
      await refetch(); // Refresh RFQ data to show new status

    } catch (err) {
      console.error('Error requesting inclusion:', err);
      alert(`Failed to request inclusion: ${err.message}`);
    } finally {
      setDraftingInclusion(false);
    }
  };


  // Handle LinkedIn draft generation
  const handleGenerateLinkedinDraft = async () => {
    try {
      setGeneratingLinkedinDraft(true);
      setLinkedinDraftError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/linkedin/${id}/draft`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to generate LinkedIn draft');
      }

      setLinkedinDraft({ content: result.content });
    } catch (err) {
      console.error('Error generating LinkedIn draft:', err);
      setLinkedinDraftError(err.message);
    } finally {
      setGeneratingLinkedinDraft(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !rfq) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading RFQ: {error || 'Not found'}</p>
        <Button onClick={() => navigate('/recruiter/rfqs')} className="mt-4">
          Back to RFQs
        </Button>
      </div>
    );
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      'inclusion_requested': 'bg-yellow-100 text-yellow-800',
      'invited': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-indigo-100 text-indigo-800',
      'submitted': 'bg-purple-100 text-purple-800',
      'pending_outcome': 'bg-orange-100 text-orange-800',
      'successful': 'bg-green-100 text-green-800',
      'unsuccessful': 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <DetailContainer>
      {/* Header */}
      <DetailContainer.Header>
        <DetailContainer.TitleSection>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
            onClick={() => navigate('/recruiter/rfqs')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <DetailContainer.Title
            title={rfq.ref_code}
            subtitle={rfq.title}
          />
        </DetailContainer.TitleSection>
        <DetailContainer.Actions>
          {rfq.status === 'not_invited' && (
            <>
              <TemplateSelector
                templateType="rfq_inclusion_request"
                label="Inclusion Template"
                onTemplateSelect={setInclusionTemplate}
                selectedTemplateId={inclusionTemplate?.id}
                variant="outline"
                size="default"
              />
              <Button
                onClick={handleRequestInclusion}
                disabled={draftingInclusion || !gmailConnected || !inclusionTemplate}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {draftingInclusion ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Drafting...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Request Inclusion
                </>
              )}
            </Button>
            </>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(`/recruiter/rfqs/${id}/matches`)}
          >
            <Target className="h-4 w-4 mr-2" />
            Find Matches
          </Button>
          <Button
            variant="outline"
            onClick={() => setManualSearchOpen(true)}
          >
            <Search className="h-4 w-4 mr-2" />
            Manual Search
          </Button>
          <Button
            variant="outline"
            onClick={handleGenerateLinkedinDraft}
            disabled={generatingLinkedinDraft}
          >
            {generatingLinkedinDraft ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Drafting...
              </>
            ) : (
              <>
                <Linkedin className="h-4 w-4 mr-2 text-[#0077B5]" />
                LinkedIn Draft
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const url = `/job/${id}`;
              console.log('Opening public job listing:', url);
              window.open(url, '_blank');
            }}
            title="View Public Display - opens in new tab"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View PD
          </Button>
          <div className="flex flex-col items-end">
            <Button
              variant="outline"
              onClick={handleDownloadDOCX}
              disabled={downloadingDOCX}
              title="Download Position Description as DOCX"
            >
              {downloadingDOCX ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4 mr-2" />
                  DOCX
                </>
              )}
            </Button>
            {docxError && (
              <span className="text-xs text-red-500 mt-1 max-w-[160px] text-right">{docxError}</span>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => setEditModalOpen(true)}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Badge className={getStatusBadgeColor(rfq.status)}>
            {rfq.status.replace(/_/g, ' ')}
          </Badge>
          {rfq.open_to_all && (
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
              Open to All
            </Badge>
          )}
          {!rfq.display_status && (
            <Badge variant="outline" className="text-xs">
              Display status not set
            </Badge>
          )}
        </DetailContainer.Actions>
      </DetailContainer.Header>

      {/* Success Message */}
      {successMessage && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* RFQ Details - 2 Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
        {/* Basic Info */}
        <Card className="w-full max-w-full overflow-hidden">
          <CardHeader>
            <CardTitle>RFQ Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Client</p>
              <div className="mt-1 flex items-center">
                <Building className="h-4 w-4 text-muted-foreground mr-2" />
                <p>{rfq.client?.name || 'Not specified'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Security Clearance Required</p>
              <div className="mt-1">
                {rfq.clearance_required ? (
                  <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">{rfq.clearance_required}</Badge>
                ) : (
                  <p>None</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Work Arrangement</p>
              <p className="mt-1">{rfq.work_arrangement || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Locations</p>
              <div className="mt-1 flex items-start">
                <MapPin className="h-4 w-4 text-muted-foreground mr-2 mt-0.5" />
                <div>
                  {rfq.locations ? (
                    Array.isArray(rfq.locations) ? (
                      rfq.locations.map((loc, idx) => (
                        <p key={idx}>{loc}</p>
                      ))
                    ) : (
                      <p>{JSON.stringify(rfq.locations)}</p>
                    )
                  ) : (
                    <p>Not specified</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dates & Terms */}
        <Card className="w-full max-w-full overflow-hidden">
          <CardHeader>
            <CardTitle>Dates & Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Internal Close Date</p>
              <div className="mt-1 flex items-center">
                <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
                <p>{formatDate(rfq.internal_close_datetime, { style: 'long' })}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Client Close Date</p>
              <div className="mt-1 flex items-center">
                <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
                <p>{formatDate(rfq.client_close_datetime, { style: 'long' })}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Estimated Start Date</p>
              <p className="mt-1">{formatDate(rfq.estimated_start_date, { style: 'long' })}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Initial Term</p>
              <p className="mt-1">
                {rfq.initial_term_months
                  ? `${rfq.initial_term_months} months`
                  : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Extension Options</p>
              <p className="mt-1">{rfq.extension_options || 'Not specified'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Match Profile */}
      <MatchProfileCard rfqId={id} />

      {/* Applications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Applications
          </CardTitle>
          <CardDescription>
            {orderedApplications.length} candidate{orderedApplications.length !== 1 ? 's' : ''} applied
            {orderedApplications.length > 0 && (
              <span className="text-xs ml-2">(Drag rows to reorder)</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {orderedApplications.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30px]"></TableHead>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Clearance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
                      const endIndex = startIndex + ITEMS_PER_PAGE;
                      const paginatedApps = orderedApplications.slice(startIndex, endIndex);

                      return paginatedApps.map((app, index) => {
                        const actualIndex = startIndex + index;
                        return (
                        <TableRow
                          key={app.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, actualIndex)}
                          onDragOver={(e) => handleDragOver(e, actualIndex)}
                          onDragEnd={handleDragEnd}
                          className={`${
                            draggedIndex === actualIndex ? 'opacity-50' : ''
                          } cursor-move hover:bg-accent`}
                        >
                      <TableCell className="cursor-grab active:cursor-grabbing">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                      <TableCell className="font-medium">
                        {app.candidate?.first_name} {app.candidate?.last_name}
                      </TableCell>
                      <TableCell>{app.candidate?.email || '-'}</TableCell>
                      <TableCell>{app.candidate?.phone || '-'}</TableCell>
                      <TableCell>
                        {app.asking_rate_value ? (
                          <span>${app.asking_rate_value}/{app.asking_rate_basis}</span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {app.candidate?.location_city && app.candidate?.location_state
                          ? `${app.candidate.location_city}, ${app.candidate.location_state}`
                          : app.candidate?.location_city || app.candidate?.location_state || '-'}
                      </TableCell>
                      <TableCell>
                        {app.candidate?.clearance_level ? (
                          <Badge variant="outline" className="text-xs">
                            {app.candidate.clearance_level}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <select
                          value={app.status}
                          onChange={(e) => handleStatusChange(app.id, e.target.value)}
                          className="text-xs border rounded px-2 py-1 bg-background cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="submitted">Submitted</option>
                          <option value="reviewing">Reviewing</option>
                          <option value="shortlisted">Shortlisted</option>
                          <option value="interviewing">Interviewing</option>
                          <option value="offered">Offered</option>
                          <option value="accepted">Accepted</option>
                          <option value="rejected">Rejected</option>
                          <option value="unsuitable">Unsuitable</option>
                          <option value="withdrawn">Withdrawn</option>
                        </select>
                      </TableCell>
                          <TableCell
                            className="cursor-pointer hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/recruiter/applications/${app.id}`);
                            }}
                          >
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {orderedApplications.length > ITEMS_PER_PAGE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to{' '}
                    {Math.min(currentPage * ITEMS_PER_PAGE, orderedApplications.length)} of{' '}
                    {orderedApplications.length} applications
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {currentPage} of {Math.ceil(orderedApplications.length / ITEMS_PER_PAGE)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(orderedApplications.length / ITEMS_PER_PAGE), prev + 1))}
                      disabled={currentPage === Math.ceil(orderedApplications.length / ITEMS_PER_PAGE)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">No applications yet</p>
          )}
        </CardContent>
      </Card>

      {/* Custom Application Questions */}
      {rfq.client_id && (
        <RFQQuestionsManager rfqId={id} clientId={rfq.client_id} />
      )}

      {/* Description */}
      {rfq.description && (
        <Card className="w-full max-w-full overflow-hidden">
          <CardHeader>
            <CardTitle>Position Description</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={PROSE}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanHTML(rfq.description)) }}
            />
          </CardContent>
        </Card>
      )}

      {/* Selection Criteria */}
      {rfq.selection_criteria && (
        <Card className="w-full max-w-full overflow-hidden">
          <CardHeader>
            <CardTitle>Selection Criteria</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={PROSE}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fixSelectionCriteriaNumbering(cleanHTML(rfq.selection_criteria))) }}
            />
          </CardContent>
        </Card>
      )}

      {/* Client Details */}
      {rfq.client && (
        <Card className="w-full max-w-full overflow-hidden">
          <CardHeader>
            <CardTitle>Client Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Company Name</p>
                <p className="mt-1">{rfq.client.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">ABN</p>
                <p className="mt-1">{rfq.client.abn || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">RCTI Client</p>
                <Badge className={rfq.client.is_rcti_client ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'}>
                  {rfq.client.is_rcti_client ? 'Yes' : 'No'}
                </Badge>
              </div>
            </div>
            {rfq.client.notes && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium text-muted-foreground mb-2">Client Notes</p>
                <p className="whitespace-pre-wrap">{rfq.client.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      <AddRFQModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        onSuccess={(data, message) => {
          refetch();
          setEditModalOpen(false);
          setSuccessMessage(message);
        }}
        editMode={true}
        existingRFQ={rfq}
      />

      {/* Manual Candidate Search Modal */}
      <ManualCandidateSearch
        open={manualSearchOpen}
        onOpenChange={setManualSearchOpen}
        rfq={rfq}
        gmailConnected={gmailConnected}
      />

      {/* LinkedIn Draft Preview Dialog */}
      <Dialog
        open={!!linkedinDraft || !!linkedinDraftError}
        onOpenChange={() => {
          setLinkedinDraft(null);
          setLinkedinDraftError(null);
          setCopiedLinkedinDraft(false);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Linkedin className="h-5 w-5 text-[#0077B5]" />
              LinkedIn Draft
            </DialogTitle>
            {!linkedinDraftError && (
              <DialogDescription>
                Copy and post manually to LinkedIn.
              </DialogDescription>
            )}
          </DialogHeader>
          {linkedinDraftError ? (
            <Alert className="bg-red-50 border-red-200">
              <AlertDescription className="text-red-800">
                {linkedinDraftError}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="bg-muted rounded-md p-4 text-sm whitespace-pre-wrap max-h-[50vh] overflow-y-auto">
              {linkedinDraft?.content}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setLinkedinDraft(null);
                setLinkedinDraftError(null);
                setCopiedLinkedinDraft(false);
              }}
            >
              Close
            </Button>
            {!linkedinDraftError && (
              <Button
                onClick={() => {
                  if (linkedinDraft?.content) {
                    navigator.clipboard.writeText(linkedinDraft.content).then(() => {
                      setCopiedLinkedinDraft(true);
                      setTimeout(() => setCopiedLinkedinDraft(false), 2000);
                    });
                  }
                }}
              >
                {copiedLinkedinDraft ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DetailContainer>
  );
};

export default RFQDetail;
